import { environment } from 'environment';
import { Component, effect, HostListener, inject, OnDestroy, OnInit, Signal, DOCUMENT, ChangeDetectionStrategy, untracked } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from '@services/auth/auth.service';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { HelperService } from '@services/helper/helper.service';
import { MaintenanceService } from '@services/auth/maintenance.service';
import { TranslateService } from '@ngx-translate/core';
import { Crisp } from 'crisp-sdk-web';
import { ChatbotService } from '@services/chatbot/chatbot.service';
import { filter } from 'rxjs/operators';
import { Language, locales } from '@constants/supportedLanguages';
import { NgSelectConfig } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import * as en from 'dayjs/locale/en';
import * as en_gb from 'dayjs/locale/en-gb';
import updateLocale from 'dayjs/plugin/updateLocale';
import { LoadingComponent } from '@components/loading/loading.component';
import { SidebarComponent } from './navigation/nav-sidebar/sidebar.component';
import { SidebarService } from '@services/sidebar/sidebar.service';
import { AdminSidebarComponent } from './navigation/admin-sidebar/admin-sidebar.component';
import { TopbarComponent } from './navigation/topbar/topbar.component';
import { SearchBarComponent } from '@components/search/search-bar/search-bar.component';
import { Title } from '@angular/platform-browser';

import { FeatureService } from '@services/feature-toggle/feature.service';
import { AW } from '@airwallet/types';
import { GuidedTrialResumeComponent } from '@components/misc/guided-trial/guided-trial-resume.component';
dayjs.extend(updateLocale);

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterOutlet, LoadingComponent, SidebarComponent, AdminSidebarComponent, TopbarComponent, SearchBarComponent, GuidedTrialResumeComponent]
})
export class AppComponent implements OnInit, OnDestroy {
    maintenanceSub: Subscription;
    isAdmin: Signal<boolean> = this.authService.isAdmin;
    isSignedIn: Signal<boolean> = this.authService.isSignedIn;
    isReady: Signal<boolean> = this.featureService.accessMapReady;
    /**
     * HostListener for window resize events.
     * Adjusts the sidebar visibility based on the current screen width.
     */
    @HostListener('window:resize')
    onResize(): void {
        this.sidebarService.adjustSidebarForScreenWidth(window.innerWidth);
    }
    /**
     * HostListener for document click events.
     * Closes the sidebars if the click occurs on 'main-content-overlay'.
     *
     * @param event - The mouse click event triggered on the document.
     */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        this.sidebarService.closeSidebars(event);
    }

    /**
     * Handles synchronization of user state across multiple browser tabs.
     *
     * This listener triggers whenever `localStorage` is modified in a *different* tab or window.
     * It acts as a safety guardrail for the impersonation feature.
     *
     * **The Problem:**
     * Since `localStorage` is shared across tabs, switching the `authenticatedUser` (admin controlling)
     * in Tab A invalidates the in-memory state of Tab B. If Tab B continues to hold
     * stale credentials, it may save data to the wrong account or crash.
     *
     * **The Solution:**
     * If the 'authenticatedUser' key changes externally:
     * 1. Detects the mismatch between the current tab's state and the new storage state.
     * 2. Forces a safe exit (reload) to prevent data corruption.
     *
     * @param event - The native DOM StorageEvent containing the key changed, old value, and new value.
     */
    @HostListener('window:storage', ['$event'])
    onStorageChange(event: StorageEvent) {
        // We only care if the auth or dashboard user object was changed by another tab
        if (event.key === 'authenticatedUser' || event.key === 'dashboardOwnerUser') {
            // Check if the new value is actually different from what we currently have in memory
            const newValue = event.newValue;
            const oldValue = event.oldValue;

            if (newValue !== oldValue) {
                // Reload the page so this tab syncs with the new user
                window.location.reload();
            }
        }
    }

    private routeSubscription!: Subscription;
    document = inject(DOCUMENT);
    constructor(
        protected authService: AuthService,
        protected featureService: FeatureService,
        private localStorageService: LocalStorageService,
        private helperService: HelperService,
        private maintenanceService: MaintenanceService,
        private chatbotService: ChatbotService,
        private config: NgSelectConfig,
        private translate: TranslateService,
        protected sidebarService: SidebarService,
        private router: Router,
        private titleService: Title
    ) {
        // below is translations set in the config for ng-select to use in the entire app
        this.config.notFoundText = this.translate.instant('ng_select.not_found');
        this.config.typeToSearchText = this.translate.instant('ng_select.type_to_search');
        this.config.addTagText = this.translate.instant('ng_select.add_item');
        this.config.loadingText = this.translate.instant('ng_select.loading');
        this.config.clearAllText = this.translate.instant('ng_select.clear_all');

        this.maintenanceSub = this.helperService.getMaintenanceState().subscribe(state => {
            const maintenance: AW.Misc.Period = state.snapshot.val();
            this.maintenanceService.maintenance.set(maintenance);
            this.maintenanceService.checkingForMaintenance.set(false);
        });

        effect(() => {
            if (this.maintenanceService.isUnderMaintenance() && !window.location.href.includes('sign-in')) {
                untracked(() => this.authService.signOut());
            }
        });
    }

    async ngOnInit() {
        this.measureScrollbarWidth();

        const script = this.document.createElement('script');
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${environment.recaptchaSiteKey}`;
        script.async = true;
        script.defer = true;
        this.document.head.appendChild(script);
        this.sidebarService.adjustSidebarForScreenWidth(window.innerWidth);

        Crisp.configure(environment.crispWebsiteId, {
            cookieExpire: 3600 * 24,
            locale: this.localStorageService.getItem('language')?.language_code ?? 'en'
        });

        Crisp.setZIndex(1040);
        Crisp.session.setSegments(['dashboard'], true);
        Crisp.session.onLoaded(async () => {
            this.authService.permissionsReady.then(() => {
                this.chatbotService.setupChatBot();
            });
        });

        $crisp.push(['safe', true]);

        if (!window.location.pathname.includes('/sign-in')) {
            this.localStorageService.setItem('callbackUrl', window.location.pathname); // trying to access a guarded page, will remember for redirect after sign in.
        }

        this.setDayjsLocale();
        if (window.location.pathname.includes('/reset-password') || window.location.pathname.includes('/set-password') || (window.location.pathname.includes('/member') && !window.location.pathname.includes('/members'))) {
            // user is signed in and trying to access an unguarded page, our ungarded pages are used for non-authenticated users, so we sign them out;
            // this is used for member/subcustomer's invitation links, and sign-up
            await this.authService.signOut(true);
        }

        this.routeSubscription = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
            setTimeout(() => {
                this.updateActiveNavLink();
            }, 0);
            // The page scrolls inside `.main-content-item`, not the document,
            // so the router's built-in `withInMemoryScrolling` can't reset
            // it for us. Snap it back to the top on every navigation so a
            // new route always opens at the start instead of inheriting the
            // previous page's scroll position.
            this.scrollContainerToTop();
        });
    }

    private scrollContainerToTop() {
        document.querySelector<HTMLElement>('.main-content-item')?.scrollTo({ top: 0 });
    }

    /**
     * Sets `--scrollbar-width` on the document root by measuring a throwaway
     * scroll container. Used by `.search-top` (and anything else that needs
     * to align with `.main-content-item`'s reserved scrollbar gutter) to
     * mirror the gutter width without `scrollbar-gutter: stable` — that
     * property only works on actual scroll containers, which would clip
     * the search-bar dropdown that lives inside `.search-top`.
     *
     * Resolves to 0 on browsers with overlay scrollbars (macOS) — exactly
     * what we want, since there's no real gutter to align against in that
     * case either.
     */
    private measureScrollbarWidth(): void {
        const probe = this.document.createElement('div');
        probe.style.cssText = 'position:absolute;top:-9999px;left:-9999px;width:100px;height:100px;overflow:scroll;';
        this.document.body.appendChild(probe);
        const width = probe.offsetWidth - probe.clientWidth;
        this.document.body.removeChild(probe);
        this.document.documentElement.style.setProperty('--scrollbar-width', `${width}px`);
    }

    ngOnDestroy() {
        // Unsubscribe to avoid memory leaks
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }
    }

    updateActiveNavLink() {
        const currentUrl = this.router.url;

        // Update the isFullScreenRoute signal in SidebarService
        this.sidebarService.isFullScreenRoute.set(this.sidebarService.fullScreenRoutes.some(route => currentUrl.includes(route)));

        // Remove 'active' class from all nav links
        const navLinks: NodeListOf<Element> = document.querySelectorAll('.nav-link');
        navLinks.forEach(link => link.classList.remove('active'));

        // Find the last valid ID in the URL that matches an actual nav-link ID
        const urlSegments: string[] = currentUrl.split(/[/?]/); // Splits url by '/' and '?'
        const validIds: string[] = Array.from(navLinks).map(link => link.getAttribute('id') ?? '');

        // Reverse the segments to find the last valid ID (I.e. when navigating to /locations/xxx/devices/xxx, we want to find 'nav-devices' last)
        const lastId: string | undefined = urlSegments.reverse().find(segment => validIds.includes(`nav-${segment}`));
        const isSearchPage = currentUrl.startsWith('/search');

        if (!lastId && !isSearchPage) {
            return;
        }

        // Add 'active' class to the link matching the last id
        const targetId = `nav-${lastId}`;
        navLinks.forEach(link => {
            const route: string | null = link.getAttribute('id');
            if (route === targetId) {
                link.classList.add('active');
            }
        });
        // Update the page title
        const activeRouteSnapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root.firstChild;
        if (activeRouteSnapshot) {
            const title: string = this.getTitle(activeRouteSnapshot);
            this.titleService.setTitle(title);
        }
    }

    getTitle(next: ActivatedRouteSnapshot): string {
        const path: string = next.routeConfig?.path ?? '';

        if (!next.queryParams['name'] && !next.queryParams['q'] && !next.queryParams['interval']) {
            const pathArr: string[] = path.split('/').filter(segment => !segment.startsWith(':'));
            return this.translate.instant(`navbar.${pathArr[pathArr.length - 1]}`);
        }

        if (next.queryParams['interval']) {
            return this.translate.instant('navbar.subscription-plan/payment');
        }

        if (next.queryParams['q']) {
            return this.translate.instant('search.common.search_results') + ` - ${next.queryParams['q']}`;
        }

        return `${next.queryParams['name']}`;
    }

    async setDayjsLocale() {
        let browserLang = navigator.language;

        const lang: Language | undefined = this.localStorageService.getItem('language') ?? undefined;
        if (lang) {
            browserLang = lang.language_code;
        }

        let LOCALE = browserLang.split('-')[0];
        let end: string | undefined = browserLang.split('-')[1];
        if (navigator.language.substring(0, 2) === 'nb' || navigator.language.substring(0, 2) === 'nn') {
            LOCALE = 'no';
            end = undefined;
        }

        const fullString = (end ? LOCALE + '_' + end : LOCALE).toLocaleLowerCase();
        if (locales[fullString as keyof typeof locales] === undefined) {
            if (browserLang === 'en-US') {
                const localeObject = { ...en };

                dayjs.locale('en-us', localeObject);
            }
        } else {
            // Update Dayjs
            dayjs.updateLocale('en', { ...en_gb });
            dayjs.locale(fullString);
        }
    }
}
